import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { PageHeader, Spinner, StatusBadge } from "@/components/site/Bits";
import { ApplyForm, FileUploader, REVIEW_TARGET, UFile, uploadKey } from "@/components/site/PartnerForm";

const Respond = ({ token, onSent }: { token: string; onSent: () => void }) => {
  const [msg, setMsg] = useState("");
  const [docs, setDocs] = useState<UFile[]>([]);
  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/partners/portal/respond?t=${encodeURIComponent(token)}&upload_key=${uploadKey()}`, { message: msg, document_ids: docs.map((d) => d.id) });
      toast.success("Sent to our team");
      setMsg(""); setDocs([]); onSent();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={send} className="card-lux space-y-3 p-6" data-testid="portal-respond">
      <p className="font-semibold">Reply or send requested documents</p>
      <textarea required minLength={2} rows={3} value={msg} onChange={(e) => setMsg(e.target.value)} className="field" data-testid="portal-respond-message" />
      <FileUploader kind="document" testid="portal-docs" files={docs} setFiles={setDocs} />
      <button className="btn-gold" data-testid="portal-respond-submit">Send</button>
    </form>
  );
};

const Contact = ({ token, a, onSaved }: { token: string; a: any; onSaved: () => void }) => {
  const [c, setC] = useState({ phone: a.applicant.phone || "", whatsapp: a.applicant.whatsapp || "", preferred_contact: a.applicant.preferred_contact || "email" });
  const save = async () => {
    try { await api.put(`/partners/portal/contact?t=${encodeURIComponent(token)}`, c); toast.success("Contact details updated"); onSaved(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="card-lux space-y-3 p-6" data-testid="portal-contact">
      <p className="font-semibold">Contact details</p>
      <input value={c.phone} onChange={(e) => setC({ ...c, phone: e.target.value })} placeholder="Phone" className="field" data-testid="portal-phone" />
      <input value={c.whatsapp} onChange={(e) => setC({ ...c, whatsapp: e.target.value })} placeholder="WhatsApp" className="field" data-testid="portal-whatsapp" />
      <select value={c.preferred_contact} onChange={(e) => setC({ ...c, preferred_contact: e.target.value })} className="field" data-testid="portal-preferred"><option value="email">Email</option><option value="phone">Phone</option><option value="whatsapp">WhatsApp</option></select>
      <button onClick={save} className="btn-ghost" data-testid="portal-contact-save">Save</button>
    </div>
  );
};

export default function PartnerPortal() {
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const [a, setA] = useState<any>(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    api.get(`/partners/portal?t=${encodeURIComponent(token)}`).then((r) => setA(r.data)).catch((e) => setErr(errMsg(e)));
  }, [token]);
  useEffect(() => {
    load();
  }, [load]);
  if (err) return <div className="container-x py-24"><p className="card-lux p-8" data-testid="portal-error">{err} <Link to="/partner" className="underline">Back to partnerships</Link></p></div>;
  if (!a) return <Spinner />;
  const files: UFile[] = a.files || [];
  const initial = { ...a, photos: files.filter((f) => a.photo_ids?.includes(f.id)), documents: files.filter((f) => a.document_ids?.includes(f.id)) };
  const withdraw = async () => {
    if (!window.confirm("Withdraw this application?")) return;
    try { await api.post(`/partners/portal/withdraw?t=${encodeURIComponent(token)}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader eyebrow={`Partner portal · ${a.reference}`} title="Your application" testid="portal-title" intro={REVIEW_TARGET} />
      <section className="container-x grid gap-8 py-12 lg:grid-cols-[1fr_360px]">
        <div className="space-y-6">
          <div className="card-lux flex flex-wrap items-center justify-between gap-3 p-6">
            <div><p className="label">Status</p><StatusBadge status={a.status} testid="portal-status" /></div>
            <p className="text-xs text-navy/55">{a.submitted_at ? `Submitted ${fmtDate(a.submitted_at)}` : "Draft — not submitted yet"}</p>
          </div>
          {a.decision_message && <div className="card-lux p-6" data-testid="portal-decision"><p className="label">Latest message from our team</p><p className="whitespace-pre-line text-sm">{a.decision_message}</p></div>}
          {a.editable ? <ApplyForm category={a.category} initial={initial} token={token} onDone={() => setA((p: any) => ({ ...p, status: "submitted", submitted_at: new Date().toISOString() }))} /> : (
            <div className="card-lux p-6 text-sm text-navy/65" data-testid="portal-locked">Your application is with our team and can no longer be edited. Use the reply box to send updates or documents.</div>
          )}
          {a.messages?.length > 0 && (
            <div className="card-lux space-y-3 p-6" data-testid="portal-messages"><p className="font-semibold">Messages</p>
              {a.messages.map((m: any, i: number) => <div key={i} className={`rounded-xl p-3 text-sm ${m.from === "team" ? "bg-brass-soft" : "bg-stone-canvas"}`}><p className="text-xs text-navy/50">{m.from === "team" ? "Trust Logistics" : "You"} · {fmtDate(m.at)}</p><p className="whitespace-pre-line">{m.message}</p></div>)}
            </div>
          )}
        </div>
        <aside className="space-y-6">
          {a.status !== "draft" && !["withdrawn", "archived", "declined"].includes(a.status) && <Respond token={token} onSent={load} />}
          <Contact token={token} a={a} onSaved={load} />
          {!["withdrawn", "archived", "declined"].includes(a.status) && <button onClick={withdraw} className="text-sm text-red-600 underline" data-testid="portal-withdraw">Withdraw application</button>}
        </aside>
      </section>
    </div>
  );
}
